import BadgeNumbers from "@/Components/UI/BadgeNumbers";
import classNames from "@/utils/classNames";
import useOutsideClick from "@/utils/useOutsideClick";
import { useRef } from "react";
import { NavItem } from "./types";

type MoreMenuProps = {
    items: NavItem[];
    isItemSelected: (item: NavItem) => boolean;
    onItemActivated: (item: NavItem) => void;
    onDismiss: () => void;
};

export default function MoreMenu({
    items,
    isItemSelected,
    onItemActivated,
    onDismiss,
}: MoreMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    useOutsideClick(menuRef, onDismiss);

    return (
        <div
            ref={menuRef}
            data-testid="more-options-list"
            className="absolute -top-px flex w-full -translate-y-full flex-col bg-brand-white drop-shadow-top"
        >
            {items.map((item) => (
                <MoreMenuItem
                    key={item.name}
                    item={item}
                    selected={isItemSelected(item)}
                    onActivate={() => onItemActivated(item)}
                />
            ))}
        </div>
    );
}

type MoreMenuItemProps = {
    item: NavItem;
    selected: boolean;
    onActivate: () => void;
};

function MoreMenuItem({ item, selected, onActivate }: MoreMenuItemProps) {
    return (
        <div
            data-testid={`more-options-${item.name.replace(/\s+/g, "-").toLowerCase()}`}
            className={classNames(
                "flex grow items-center gap-4 px-4 py-2 transition-transform active:scale-95",
                selected ? "text-brand-red" : "text-brand-black",
            )}
            onClick={onActivate}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onActivate()}
        >
            <item.icon className="my-2 cursor-pointer rounded-md text-2xl" />
            <div className="relative text-lg font-medium">
                {item.name}
                {item.badgeNumbers && (
                    <BadgeNumbers
                        badgeNumbers={item.badgeNumbers}
                        badgeStyles={[
                            "-right-[18px] top-[0px] text-brand-white bg-brand-red",
                            "-right-[28px] top-[0px] text-brand-white bg-brand-dangerOrange",
                        ]}
                    />
                )}
            </div>
        </div>
    );
}
